import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import {
  Box,
  Select,
  MenuItem,
  Typography,
  IconButton,
  CircularProgress,
  DialogActions,
  Slider,
  Menu,
  Alert,
} from "@mui/material";
import { height, styled } from "@mui/system";
import {
  CloudUpload,
  CameraAlt,
  PlayArrow,
  Pause,
  WhatsApp,
  Facebook,
  Sms,
  VolumeUp,
  ArrowDropDown,
  Home,
} from "@mui/icons-material";
import { Button, Dialog, DialogTitle, DialogContent } from "@mui/material";
import pdf from "../image/invoices.pdf";
import Cropper from "react-easy-crop";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import LogoResponsive from "../image/AgriHyphen AI.png";
import recommendations from "./recommandations";
import { languageFlags } from "../i18n";
import { useSpeechSynthesis } from "react-speech-kit";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import PolicyDialog from "../components/PolicyDialog";
import GoogleFormsDialog from "../components/GoogleFormsDialog";
import { Close as CloseIcon } from "@mui/icons-material";
import DonatePage from "./DonatePage";
import { getAudioUrl } from "google-tts-api";
import demoVideo from "../image/demo-video.mp4"; // Adjust path as needed
import ReactDOM from "react-dom";

// Animated buttons for feedback and donation
const buttonVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const formUrls = {
  en: "https://docs.google.com/forms/d/e/1FAIpQLSfg4agy9D9P3A6wvMSlxS-JPuJ80PdLjygsZOi3_t7m086-Wg/viewform?embedded=true",
  fr: "https://docs.google.com/forms/d/e/1FAIpQLSdbL-Yf19vt8PO50FM2gHOTUr4oMQbuR1XfDcWPrh5G9abMXQ/viewform?embedded=true",
  swa: "https://docs.google.com/forms/d/e/1FAIpQLSdbL-Yf19vt8PO50FM2gHOTUr4oMQbuR1XfDcWPrh5G9abMXQ/viewform?embedded=true", // French form
  kir: "https://docs.google.com/forms/d/e/1FAIpQLSdbL-Yf19vt8PO50FM2gHOTUr4oMQbuR1XfDcWPrh5G9abMXQ/viewform?embedded=true", // French form
};

const getFormUrl = (language) => {
  switch (language) {
    case "kir":
      return formUrls["fr"]; // Default to French for Kirundi
    case "swa":
      return formUrls["en"]; // Default to English for Swahili
    default:
      return formUrls[language] || formUrls["en"]; // Default to English if language not found
  }
};

/* ------------------------------------------------------------------
   Detection screen — styled primitives.
   Tokens mirror tailwind.config.js so the tool and the marketing site
   read as one product: warm paper ground, greened ink, clay accent.
   ------------------------------------------------------------------ */
const T = {
  paper: "#FAF8F3",
  paperDeep: "#F2EEE4",
  card: "#FFFFFF",
  ink: "#14180F",
  ink80: "#3A4034",
  ink60: "#5A6151",
  ink40: "#6E7565",
  rule: "#DED8C9",
  forest: "#13301D",
  forestDeep: "#0C2114",
  moss: "#2C6B41",
  clay: "#A8482A",
  lime: "#CDFB5A",
  display: "'Fraunces', Georgia, serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

const NAV_H = 64;

// Model options — one trained detector per supported crop
const MODELS = [
  { value: "apple", label: "Apple Disease Detection" },
  { value: "beans", label: "Beans Disease Detection" },
  { value: "cassava", label: "Cassava Disease Detection" },
  { value: "maize", label: "Maize Disease Detection" },
  { value: "pepper", label: "Pepper Disease Detection" },
  { value: "potato", label: "Potato Disease Detection" },
  { value: "tomatoes", label: "Tomatoes Disease Detection" },
];

const Container = styled(Box)({
  minHeight: "100vh",
  backgroundColor: T.paper,
  color: T.ink,
  display: "flex",
  flexDirection: "column",
});

const Header = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: `${NAV_H}px`,
  padding: "0 32px",
  backgroundColor: "rgba(250, 248, 243, 0.94)",
  backdropFilter: "blur(12px)",
  borderBottom: `1px solid ${T.rule}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  zIndex: 1000,
  "@media (max-width: 768px)": {
    padding: "0 16px",
    gap: "8px",
  },
});

const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexShrink: 0,
});

const Wordmark = styled(Typography)({
  fontFamily: T.display,
  fontWeight: 600,
  fontSize: "1.0625rem",
  letterSpacing: "-0.02em",
  color: T.ink,
  whiteSpace: "nowrap",
  "@media (max-width: 520px)": { display: "none" },
});

/* Model picker sits centred between the wordmark and the controls */
const SelectContainer = styled(Box)({
  flex: "1 1 auto",
  display: "flex",
  justifyContent: "center",
  minWidth: 0,
});

const MainContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: "880px",
  margin: "0 auto",
  padding: `${NAV_H + 48}px 24px 48px`,
  "@media (max-width: 768px)": {
    padding: `${NAV_H + 32}px 20px 32px`,
  },
});

const UploadArea = styled(Box)({
  backgroundColor: T.card,
  padding: "56px 32px",
  textAlign: "center",
  cursor: "pointer",
  transition: "border-color 0.25s ease, background-color 0.25s ease",
  border: `1px dashed ${T.rule}`,
  "&:hover": {
    backgroundColor: T.paperDeep,
    borderColor: T.ink,
  },
  "@media (max-width: 768px)": {
    padding: "40px 20px",
  },
});

const StyledSelect = styled(Select)({
  minWidth: 240,
  maxWidth: 320,
  color: T.ink,
  backgroundColor: T.card,
  textAlign: "left",
  border: `1px solid ${T.rule}`,
  borderRadius: 0,
  transition: "border-color 0.2s ease",
  "& .MuiSelect-select": {
    padding: "11px 14px",
    color: T.ink,
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  "& .MuiSelect-icon": { color: T.ink40 },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover": { borderColor: T.ink },
  "@media (max-width: 768px)": {
    minWidth: 0,
    "& .MuiSelect-select": { padding: "9px 10px", fontSize: "0.8rem" },
  },
});

const FlagIcon = styled("img")({
  width: 22,
  height: 22,
  borderRadius: "50%",
  cursor: "pointer",
  objectFit: "cover",
});

const ImagePreviewContainer = styled(Box)({
  overflow: "hidden",
  border: `1px solid ${T.rule}`,
  backgroundColor: T.paperDeep,
});

const PreviewImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const ResultContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
});

const RecommendationsContainer = styled(Box)({
  width: "100%",
  backgroundColor: T.card,
  border: `1px solid ${T.rule}`,
  padding: "28px",
  "@media (max-width: 768px)": { padding: "20px" },
});

/* Section kicker — the mono label used across the whole site */
const Kicker = styled(Typography)({
  fontFamily: T.mono,
  fontSize: "0.6875rem",
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.14em",
  color: T.ink60,
  display: "flex",
  alignItems: "center",
  gap: "12px",
  "&::before": {
    content: '""',
    display: "block",
    width: "24px",
    height: "1px",
    backgroundColor: T.clay,
    flexShrink: 0,
  },
});

const PageTitle = styled(Typography)({
  fontFamily: T.display,
  fontWeight: 600,
  fontSize: "2.5rem",
  lineHeight: 1.06,
  letterSpacing: "-0.03em",
  color: T.ink,
  maxWidth: "18ch",
  "@media (max-width: 600px)": { fontSize: "2rem" },
});

const Lede = styled(Typography)({
  fontSize: "1.0625rem",
  lineHeight: 1.65,
  color: T.ink80,
  maxWidth: "38rem",
});

const HomeButton = styled(IconButton)({
  color: T.ink60,
  border: `1px solid ${T.rule}`,
  borderRadius: 0,
  padding: "9px",
  transition: "border-color 0.2s ease, color 0.2s ease",
  "&:hover": {
    backgroundColor: "transparent",
    borderColor: T.ink,
    color: T.ink,
  },
});

const Footer = styled(Box)({
  textAlign: "center",
  padding: "28px 20px",
  marginTop: "auto",
  borderTop: `1px solid ${T.rule}`,
  fontFamily: T.mono,
  fontSize: "0.6875rem",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: T.ink40,
});

const FooterLink = styled("a")({
  color: T.ink60,
  textDecoration: "none",
  fontWeight: 500,
  borderBottom: `1px solid ${T.rule}`,
  transition: "color 0.2s ease, border-color 0.2s ease",
  "&:hover": { color: T.clay, borderColor: T.clay },
});

/* Shared button styles. One primary per screen; everything else recedes. */
const actionButtonSx = {
  minWidth: "150px",
  minHeight: "44px",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: 0,
  textTransform: "none",
  boxShadow: "none",
  backgroundColor: T.ink,
  color: T.paper,
  transition: "background-color 0.2s ease",
  "&:hover": { backgroundColor: T.forest, boxShadow: "none" },
};

const secondaryButtonSx = {
  ...actionButtonSx,
  backgroundColor: "transparent",
  color: T.ink,
  border: `1px solid ${T.rule}`,
  "&:hover": {
    backgroundColor: "transparent",
    borderColor: T.ink,
    boxShadow: "none",
  },
};

export default function ImageUploadSection() {
  const { t, i18n } = useTranslation();
  const [selectedModel, setSelectedModel] = useState("");
  const [image, setImage] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [predictionError, setPredictionError] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDemo, setShowDemo] = useState(true);
  const [currentRecommendations, setCurrentRecommendations] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const { speak, cancel, speaking } = useSpeechSynthesis();
  const [audioLoading, setAudioLoading] = useState(false);
  const [modelAnchorEl, setModelAnchorEl] = useState(null);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showGoogleForm, setShowGoogleForm] = useState(false);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [openDonation, setOpenDonation] = useState(false);

  // Handle open/close of feedback and donation modals
  const handleFeedbackClick = () => setOpenFeedback(true);
  const handleDonationClick = () => setOpenDonation(true);
  const handleCloseFeedback = () => setOpenFeedback(false);
  const handleCloseDonation = () => setOpenDonation(false);

  // Add this to get current language
  const currentLanguage = i18n.language;

  // Language handling
  const handleLanguageMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageChange = (lang) => {
    localStorage.setItem("selectedLanguage", lang);
    i18n.changeLanguage(lang);
    setShowLanguageSelect(false);
    setAnchorEl(null);

    // Check if form was ever completed (regardless of language)
    const formCompleted = localStorage.getItem("formCompleted");
    if (!formCompleted) {
      setShowGoogleForm(true);
    } else {
      // If form is completed, check policy
      const policyAccepted = localStorage.getItem("policyAccepted");
      if (!policyAccepted) {
        setShowPolicy(true);
      }
    }
  };

  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setImage(URL.createObjectURL(file));
      setPreviewImage(URL.createObjectURL(file));
      setCropperOpen(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  // Cropper handlers
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const uploadImageToServer = async (imageData) => {
    try {
      const response = await axios.post(
        `https://agridata-opal.vercel.app/api/upload/image`,
        {
          image: imageData, // Base64 string
          modelName: selectedModel,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        console.log("Image uploaded successfully:", response.data.url);
        return response.data.url;
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error.message);
      throw error;
    }
  };

  const showCroppedImage = async () => {
    try {
      if (!selectedModel) {
        alert(t("Please select a model first"));
        return;
      }

      // Get the cropped image
      const croppedImage = await getCroppedImg(image, croppedAreaPixels);
      setPreviewImage(croppedImage);
      setCropperOpen(false);

      // Start prediction immediately with the cropped image
      await sendImageForPrediction(croppedImage);

      // Try uploading the image in the background (optional)
      try {
        const uploadedUrl = await uploadImageToServer(croppedImage);
        console.log("Image uploaded successfully:", uploadedUrl);
      } catch (uploadError) {
        console.warn("Image upload failed:", uploadError);
      }
    } catch (error) {
      console.error("Error processing image:", error);
    }
  };

  // Effect to update recommendations when language changes
  useEffect(() => {
    if (prediction?.class) {
      const diseaseRecommendations = recommendations[prediction.class]
        ?.advice || [
        "No recommendations for this picture. Consider retaking the picture with a clear, full display.",
      ];
      setCurrentRecommendations(diseaseRecommendations.map((key) => t(key)));
    }
  }, [i18n.language, prediction, t]);

  // Image prediction handler
  const sendImageForPrediction = async (croppedImg) => {
    if (!selectedModel) {
      alert(t("Please select a model first"));
      return;
    }

    setLoading(true);
    setPredictionLoading(true); // Add this line to show loader
    try {
      console.log("Starting prediction...");

      // Show loading overlay
      const loadingOverlay = (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: "#374151" }} />
          <Typography sx={{ color: "white", fontWeight: 500 }}>
            {t("Analyzing your image...")}
          </Typography>
        </Box>
      );

      // Add loading overlay to DOM
      const overlayDiv = document.createElement("div");
      overlayDiv.id = "loading-overlay";
      document.body.appendChild(overlayDiv);
      ReactDOM.render(loadingOverlay, overlayDiv);

      // Rest of your existing prediction code...
      const blob = await fetch(croppedImg).then((res) => res.blob());
      const formData = new FormData();
      formData.append("file", blob, "image.jpg");

      const result = await axios.post(
        `https://agrihyphen-ai.onrender.com/predict/${selectedModel}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      console.log("Prediction result:", result.data);

      setPrediction(result.data);
      const diseaseRecommendations = recommendations[result.data.class]
        ?.advice || [
        "recommendation.unknown.disease",
        "recommendation.unknown.step1",
      ];
      setCurrentRecommendations(diseaseRecommendations.map((key) => t(key)));

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error("Prediction error:", error);
      alert(t("Error processing image. Please try again."));
    } finally {
      setLoading(false);
      setPredictionLoading(false); // Hide loader
      // Remove loading overlay
      const overlay = document.getElementById("loading-overlay");
      if (overlay) {
        ReactDOM.unmountComponentAtNode(overlay);
        overlay.remove();
      }
    }
  };

  // Sharing handlers
  const handleShare = (platform) => {
    // Use the specific URL for sharing
    const baseUrl = "https://agri.hyphentech.tech/test-ai";

    // Construct invitation message with the specific URL
    const text =
      t("share.inviteMessage", {
        url: baseUrl,
      }) ||
      `Discover AgriHyphen AI - Your Smart Farming Assistant! 🌱\n\nDetect plant diseases instantly using AI technology. Try it now at: ${baseUrl}\n\n#SmartFarming #AgriTech #AI`;

    // Handle sharing based on platform
    switch (platform) {
      case "whatsapp":
        const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
          text
        )}`;
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
        break;

      case "facebook":
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          baseUrl
        )}&quote=${encodeURIComponent(text)}`;
        window.open(
          facebookUrl,
          "_blank",
          "width=600,height=400,noopener,noreferrer"
        );
        break;

      case "sms":
        if (/Android|iPhone/i.test(navigator.userAgent)) {
          // For mobile devices
          const smsUrl = `sms:?body=${encodeURIComponent(text)}`;
          window.open(smsUrl);
        } else {
          // For desktop - copy to clipboard
          navigator.clipboard.writeText(text).then(() => {
            alert(t("share.copied") || "Invitation text copied to clipboard!");
          });
        }
        break;

      default:
        console.error("Unsupported sharing platform");
    }
  };

  const handleImageCapture = (e) => {
    if (e.target.files[0]) {
      setImage(URL.createObjectURL(e.target.files[0]));
      setPreviewImage(URL.createObjectURL(e.target.files[0]));
      setCropperOpen(true);
    }
  };

  const handleSpeak = useCallback(async () => {
    if (speaking) {
      cancel();
      setIsPlaying(false);
      return;
    }

    if (!currentRecommendations.length) return;

    console.log("Current language:", i18n.language); // Debug log

    if (i18n.language === "swa") {
      // Use Google TTS for Swahili
      try {
        setAudioLoading(true);
        setIsPlaying(true);
        const text = currentRecommendations.join(". ");

        // Debug logs
        console.log("Swahili text to speak:", text);

        const url = await getAudioUrl(text, {
          lang: "sw-TZ", // Changed to more specific Swahili code
          slow: false,
          host: "https://translate.google.com",
        });

        console.log("Generated URL:", url); // Debug log

        const audio = new Audio();

        audio.addEventListener("canplaythrough", () => {
          audio.play().catch((error) => {
            console.error("Playback error:", error);
            setIsPlaying(false);
            setAudioLoading(false);
            alert(t("Error playing audio"));
          });
        });

        audio.addEventListener("ended", () => {
          setIsPlaying(false);
          setAudioLoading(false);
        });

        audio.addEventListener("error", (e) => {
          console.error("Audio error:", e);
          setIsPlaying(false);
          setAudioLoading(false);
          alert(t("Error playing audio"));
        });

        audio.src = url;
      } catch (error) {
        console.error("Error with Swahili TTS:", error);
        setIsPlaying(false);
        setAudioLoading(false);
        alert(t("Error playing audio"));
      }
    } else if (i18n.language === "en" || i18n.language === "fr") {
      // Use browser TTS for English and French
      setIsPlaying(true);
      const text = currentRecommendations.join(". ");
      const language = i18n.language === "en" ? "en-US" : "fr-FR";

      speak({
        text,
        voice: window.speechSynthesis
          .getVoices()
          .find((voice) => voice.lang.startsWith(language)),
        onEnd: () => {
          setIsPlaying(false);
        },
      });
    } else {
      // For Kirundi or unsupported languages
      alert(t("Audio not available in this language"));
    }
  }, [speaking, cancel, currentRecommendations, i18n.language, speak, t]);

  // Add useEffect to initialize language from storage
  useEffect(() => {
    const storedLang = localStorage.getItem("selectedLanguage");
    const formCompleted = localStorage.getItem("formCompleted");
    const policyAccepted = localStorage.getItem("policyAccepted");

    if (!storedLang) {
      setShowLanguageSelect(true);
    } else {
      // Language exists, update i18n if needed
      if (storedLang !== i18n.language) {
        i18n.changeLanguage(storedLang);
      }

      // Then check other conditions
      if (!formCompleted) {
        setShowGoogleForm(true);
      } else if (!policyAccepted) {
        setShowPolicy(true);
      }
    }
  }, [i18n.language]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Add handler functions
  const handleModelMenu = (event) => {
    setModelAnchorEl(event.currentTarget);
  };

  const handleModelChange = (value) => {
    setSelectedModel(value);
    setModelAnchorEl(null);
  };

  const handleFormComplete = () => {
    localStorage.setItem("formCompleted", "true");
    setShowGoogleForm(false);

    const policyAccepted = localStorage.getItem("policyAccepted");
    if (!policyAccepted) {
      setShowPolicy(true);
    }
  };

  const handlePolicyAccept = () => {
    localStorage.setItem("policyAccepted", "true");
    setShowPolicy(false);
  };

  // Create a Language Selection Dialog component
  const LanguageSelectionDialog = ({ open, onLanguageSelect }) => {
    return (
      <Dialog
        open={open}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            color: "#374151",
          }}
        >
          <Typography variant="h5" component="span" fontWeight={600}>
            Select Language / Choisir la langue
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 2,
            }}
          >
            {Object.entries(languageFlags).map(([code, { flag, label }]) => (
              <Button
                key={code}
                onClick={() => onLanguageSelect(code)}
                variant="outlined"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderColor: "#d1d5db",
                  color: "#374151",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    bgcolor: "#f3f4f6",
                  },
                }}
              >
                <img src={flag} alt={label} style={{ width: 24, height: 24 }} />
                <Typography>{label}</Typography>
              </Button>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage");
    if (storedLanguage && storedLanguage !== i18n.language) {
      i18n.changeLanguage(storedLanguage);
    }
  }, []);

  return (
    <Container>
      <LanguageSelectionDialog
        open={showLanguageSelect}
        onLanguageSelect={handleLanguageChange}
      />

      <GoogleFormsDialog
        open={showGoogleForm}
        onClose={handleFormComplete}
        language={i18n.language}
      />

      <PolicyDialog open={showPolicy} onAccept={handlePolicyAccept} />

      {!showLanguageSelect && !showGoogleForm && !showPolicy && (
        <MainContent>
          <Header>
            <LogoContainer>
              <img
                src={LogoResponsive}
                alt=""
                aria-hidden="true"
                style={{ height: 32, width: 32, objectFit: "contain" }}
              />
              <Wordmark component="span">
                AgriHyphen<span style={{ color: T.clay }}> AI</span>
              </Wordmark>
            </LogoContainer>

            <SelectContainer>
              {!isMobile ? (
                // Desktop version - keep the dropdown
                <StyledSelect
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="" disabled>
                    {t("Select a model")}
                  </MenuItem>
                  {MODELS.map((model) => (
                    <MenuItem key={model.value} value={model.value}>
                      {t(model.label)}
                    </MenuItem>
                  ))}
                </StyledSelect>
              ) : (
                // Mobile version - menu toggle
                <>
                  <Button
                    onClick={handleModelMenu}
                    sx={{
                      color: T.ink,
                      backgroundColor: T.card,
                      border: `1px solid ${T.rule}`,
                      borderRadius: 0,
                      padding: "8px 12px",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      textTransform: "none",
                      minWidth: "140px",
                      "&:hover": { backgroundColor: T.card, borderColor: T.ink },
                    }}
                    endIcon={<ArrowDropDown />}
                  >
                    {selectedModel
                      ? t(MODELS.find((m) => m.value === selectedModel)?.label)
                      : t("Select a model")}
                  </Button>
                  <Menu
                    anchorEl={modelAnchorEl}
                    open={Boolean(modelAnchorEl)}
                    onClose={() => setModelAnchorEl(null)}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        borderRadius: 0,
                        border: `1px solid ${T.rule}`,
                        boxShadow: "0 2px 4px rgba(20,24,15,0.06)",
                        minWidth: "220px",
                      },
                    }}
                  >
                    {MODELS.map((model) => (
                      <MenuItem
                        key={model.value}
                        onClick={() => handleModelChange(model.value)}
                        selected={selectedModel === model.value}
                        sx={{
                          fontSize: "0.8125rem",
                          padding: "11px 16px",
                          "&:hover": { backgroundColor: T.paperDeep },
                          "&.Mui-selected": {
                            backgroundColor: T.paperDeep,
                            fontWeight: 600,
                            "&:hover": { backgroundColor: T.paperDeep },
                          },
                        }}
                      >
                        {t(model.label)}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </SelectContainer>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <HomeButton
                onClick={() => (window.location.href = "/")}
                title={t("Go to Homepage")}
                aria-label={t("Go to Homepage")}
              >
                <Home sx={{ fontSize: "1.25rem" }} />
              </HomeButton>

              <IconButton
                onClick={handleLanguageMenu}
                aria-label={`Language: ${languageFlags[i18n.language]?.label}`}
                sx={{ borderRadius: 0 }}
              >
                <FlagIcon
                  src={languageFlags[i18n.language].flag}
                  alt=""
                  aria-hidden="true"
                />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 0,
                    border: `1px solid ${T.rule}`,
                    boxShadow: "0 2px 4px rgba(20,24,15,0.06)",
                  },
                }}
              >
                {Object.entries(languageFlags).map(
                  ([code, { flag, label }]) => (
                    <MenuItem
                      key={code}
                      onClick={() => handleLanguageChange(code)}
                      selected={i18n.language === code}
                      sx={{
                        fontSize: "0.8125rem",
                        padding: "11px 16px",
                        "&:hover": { backgroundColor: T.paperDeep },
                        "&.Mui-selected": {
                          backgroundColor: T.paperDeep,
                          fontWeight: 600,
                          "&:hover": { backgroundColor: T.paperDeep },
                        },
                      }}
                    >
                      <FlagIcon
                        src={flag}
                        alt={label}
                        style={{ marginRight: 8 }}
                      />
                      {label}
                    </MenuItem>
                  )
                )}
              </Menu>
            </Box>
          </Header>

          {(previewImage || prediction) && (
            <ResultContainer>
              <Box>
                <Kicker component="p" sx={{ mb: 2 }}>
                  {t("Analysed sample")}
                </Kicker>
                <ImagePreviewContainer
                  sx={{
                    height: { xs: "220px", sm: "260px" },
                    width: { xs: "220px", sm: "260px" },
                  }}
                >
                  <PreviewImage src={previewImage} alt={t("Analysed sample")} />
                </ImagePreviewContainer>
              </Box>

              {prediction && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <motion.div
                    variants={buttonVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleFeedbackClick}
                      sx={actionButtonSx}
                    >
                      {t("Feedback")}
                    </Button>
                  </motion.div>

                  <motion.div
                    variants={buttonVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleDonationClick}
                      sx={secondaryButtonSx}
                    >
                      {t("Donate")}
                    </Button>
                  </motion.div>
                </Box>
              )}

              {/* Feedback Form Dialog */}
              <Dialog
                open={openFeedback}
                onClose={handleCloseFeedback}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: {
                    minHeight: "80vh",
                    maxHeight: "90vh",
                    overflowY: "auto",
                  },
                }}
              >
                <DialogTitle>
                  {t("Feedback & Waiting List")}
                  <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleCloseFeedback}
                    aria-label="close"
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <iframe
                    src={formUrls[i18n.language] || formUrls["fr"]} // Default to French form if language not found
                    width="100%"
                    height="600px"
                    frameBorder="0"
                    title="Feedback Form"
                    onLoad={() => console.log("Form loaded")}
                  />
                </DialogContent>
              </Dialog>

              {/* Donation Dialog */}
              <Dialog
                open={openDonation}
                onClose={handleCloseDonation}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: {
                    minHeight: "80vh", // Give enough height for the content
                    maxHeight: "90vh", // Prevent taking full screen
                    overflowY: "auto", // Enable scrolling if content is too long
                  },
                }}
              >
                <DialogTitle
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleCloseDonation}
                    aria-label="close"
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                  <DonatePage embedded />
                </DialogContent>
              </Dialog>

              {prediction && (
                <RecommendationsContainer>
                  {/* Confidence is the headline figure — set it as one */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      gap: 2,
                      pb: 2.5,
                      borderBottom: `1px solid ${T.rule}`,
                    }}
                  >
                    <Box>
                      <Kicker component="p">{t("Confidence")}</Kicker>
                      <Typography
                        component="p"
                        sx={{
                          fontFamily: T.display,
                          fontWeight: 600,
                          fontSize: "2.25rem",
                          lineHeight: 1,
                          letterSpacing: "-0.03em",
                          fontVariantNumeric: "tabular-nums",
                          color: T.ink,
                          mt: 1.5,
                        }}
                      >
                        {prediction.confidence}
                      </Typography>
                    </Box>

                    {/* Read-aloud control, for farmers who prefer audio */}
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <IconButton
                        onClick={handleSpeak}
                        aria-label={
                          speaking
                            ? t("Pause")
                            : t("Drop or click to upload an image")
                        }
                        sx={{
                          color: T.ink,
                          border: `1px solid ${T.rule}`,
                          borderRadius: 0,
                          "&:hover": { borderColor: T.ink },
                        }}
                        disabled={
                          audioLoading ||
                          i18n.language === "kir" ||
                          i18n.language === "swa"
                        }
                      >
                        {speaking ? <Pause /> : <PlayArrow />}
                      </IconButton>
                      <VolumeUp sx={{ color: T.ink40, fontSize: "1.125rem" }} />
                      {audioLoading && (
                        <CircularProgress size={18} sx={{ color: T.moss }} />
                      )}
                      {(i18n.language === "kir" || i18n.language === "swa") && (
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            color: T.ink60,
                            maxWidth: "16rem",
                            lineHeight: 1.5,
                          }}
                        >
                          {t("Audio will be available soon in")}{" "}
                          {i18n.language === "kir" ? "Kirundi" : "Kiswahili"}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Kicker component="p" sx={{ mt: 3, mb: 2 }}>
                    {t("Recommendations")}
                  </Kicker>

                  <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0 }}>
                    {currentRecommendations.map((rec, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 + index * 0.06 }}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2rem 1fr",
                          gap: "8px",
                          padding: "12px 0",
                          borderTop:
                            index === 0 ? "none" : `1px solid ${T.rule}`,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: T.mono,
                            fontSize: "0.6875rem",
                            fontWeight: 500,
                            color: T.clay,
                            paddingTop: "4px",
                          }}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <Typography
                          sx={{
                            fontSize: "0.9375rem",
                            lineHeight: 1.7,
                            color: T.ink80,
                          }}
                        >
                          {rec}
                        </Typography>
                      </motion.li>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      mt: 3,
                      pt: 2.5,
                      borderTop: `1px solid ${T.rule}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: T.mono,
                        fontSize: "0.625rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: T.ink40,
                        mr: 1,
                      }}
                    >
                      {t("Share")}
                    </Typography>
                    {[
                      { id: "whatsapp", Icon: WhatsApp, label: "WhatsApp" },
                      { id: "facebook", Icon: Facebook, label: "Facebook" },
                      { id: "sms", Icon: Sms, label: "SMS" },
                    ].map(({ id, Icon, label }) => (
                      <IconButton
                        key={id}
                        onClick={() => handleShare(id)}
                        aria-label={`${t("Share")} — ${label}`}
                        sx={{
                          color: T.ink60,
                          borderRadius: 0,
                          "&:hover": { color: T.clay, backgroundColor: "transparent" },
                        }}
                      >
                        <Icon sx={{ fontSize: "1.25rem" }} />
                      </IconButton>
                    ))}
                  </Box>
                </RecommendationsContainer>
              )}
            </ResultContainer>
          )}

          {/* Page masthead — left-aligned, same voice as the marketing site */}
          <Box sx={{ mt: previewImage || prediction ? 10 : 0 }}>
            <Kicker component="p">{t("Plant disease detection")}</Kicker>
            <PageTitle component="h1" sx={{ mt: 3 }}>
              {t("imgtitle")}
            </PageTitle>
            <Lede component="p" sx={{ mt: 3 }}>
              {t("imgpart")}
            </Lede>
          </Box>

          <Box
            sx={{
              mt: 5,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              component="label"
              startIcon={<CameraAlt />}
              sx={actionButtonSx}
            >
              {t("Take a Picture")}
              <input
                type="file"
                hidden
                accept="image/*"
                capture="environment"
                onChange={handleImageCapture}
              />
            </Button>
          </Box>

          <UploadArea {...getRootProps()}>
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 36, color: T.ink40, mb: 2 }} />
            <Typography
              component="p"
              sx={{
                fontFamily: T.display,
                fontWeight: 600,
                fontSize: "1.125rem",
                letterSpacing: "-0.015em",
                color: T.ink,
              }}
            >
              {t("Drop or click to upload an image")}
            </Typography>
            <Typography
              sx={{
                mt: 1,
                fontFamily: T.mono,
                fontSize: "0.6875rem",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: T.ink40,
              }}
            >
              {t("Supported formats")}: JPG, PNG
            </Typography>
          </UploadArea>

          {!previewImage && !prediction && (
            <Box sx={{ mt: 8 }}>
              <Kicker component="p">
                {t("How to use Plant Disease Detection")}
              </Kicker>
              <Box
                sx={{
                  mt: 3,
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                  overflow: "hidden",
                  border: `1px solid ${T.rule}`,
                  backgroundColor: T.forestDeep,
                }}
              >
                <video
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source src={demoVideo} type="video/mp4" />
                  {t("Your browser does not support the video tag.")}
                </video>
              </Box>
            </Box>
          )}
        </MainContent>
      )}

      <Dialog
        open={cropperOpen}
        maxWidth="md"
        fullWidth
        onClose={() => {
          setCropperOpen(false);
          if (!prediction) {
            setPreviewImage(null);
          }
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: T.display,
            fontWeight: 600,
            fontSize: "1.25rem",
            letterSpacing: "-0.018em",
            color: selectedModel ? T.ink : T.clay,
            borderBottom: `1px solid ${T.rule}`,
            px: 3,
            py: 2.5,
          }}
        >
          {selectedModel ? t("Crop Image") : t("Please select a model first")}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              position: "relative",
              height: 400,
              mt: 2,
              backgroundColor: T.forestDeep,
            }}
          >
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </Box>
          <Typography
            component="label"
            htmlFor="crop-zoom"
            sx={{
              display: "block",
              mt: 3,
              fontFamily: T.mono,
              fontSize: "0.625rem",
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: T.ink40,
            }}
          >
            {t("Zoom")}
          </Typography>
          <Slider
            id="crop-zoom"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, zoom) => setZoom(zoom)}
            sx={{ mt: 1, color: T.moss }}
          />
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2.5, borderTop: `1px solid ${T.rule}`, gap: 1 }}
        >
          <Button
            onClick={() => setCropperOpen(false)}
            sx={secondaryButtonSx}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={showCroppedImage}
            variant="contained"
            disabled={!selectedModel}
            sx={actionButtonSx}
          >
            {t("Analyze Image")}
          </Button>
        </DialogActions>
      </Dialog>

      {predictionLoading && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(12, 33, 20, 0.9)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <CircularProgress sx={{ color: T.lime }} size={32} thickness={3} />
          <Typography
            sx={{
              fontFamily: T.mono,
              fontSize: "0.6875rem",
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "rgba(255,255,255,0.75)",
            }}
          >
            {t("Processing image...")}
          </Typography>
        </Box>
      )}

      {predictionError && (
        <Alert
          severity="error"
          onClose={() => setPredictionError(null)}
          sx={{
            position: "fixed",
            top: NAV_H + 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 0,
            maxWidth: "22rem",
          }}
        >
          {predictionError}
        </Alert>
      )}

      <Footer>
        Built by{" "}
        <FooterLink
          href="https://www.hyphentech.africa"
          target="_blank"
          rel="noopener noreferrer"
        >
          Hyphen Tech
        </FooterLink>{" "}
        · Ngozi, Burundi
      </Footer>
    </Container>
  );
}

// Utility function to get cropped image
async function getCroppedImg(imageSrc, croppedAreaPixels) {
  return new Promise((resolve) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );
      resolve(canvas.toDataURL("image/jpeg"));
    };
  });
}
